import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import {
    ITableHead,
    IListPageBarEnabled,
    ITenantLicenseEntries,
    IOrganizationLicenseInfo,
    OrderType,
    Order,
    FilterEnum, IActiveFilter
} from '@app/models';
import {
    handleGetTenantLicenseData,
    handleGetOrgLicenseData,
    resetState,
    setOrderByDirection,
    setOrderByProperty,
    getModalSwitch,
    resetScroll
} from '@app/store/actions';
import { TableCell } from '@material-ui/core';
import { IMenuItem, mainMenuTypes } from '@app/components/Sidebar/menuObject';
import { getMenuItemById, infiniteScrollItemsFetchLimit, translationVariable, defaultOrgId } from '@app/helpers';
import {
    PasslyTableRow,
    PasslyTableCell,
    PasslyTable
} from '@app/components';
import { ModalComponent, IModalData } from '@app/components';
import AllocateSeatsDialog from '@app/pages/licenseManager/dialogs/allocateSeats.dialog';

interface ITenantLicenseEntriesTableData {
    seats: number;
    name: string;
    active: number;
    seatsRemaining: number;
    seatsAllocated: number;
}

interface IOrganizationLicenseInfoTableData {
    organizationId: string;
    organizationName: string;
    hasLicense: boolean;
    seats: number;
    active: number;
    licenseEdition: string;
    passwordServerEnabled: boolean;
}

enum TableHeadEnum {
    organizationName = 'organizationName',
    licenseEdition = 'licenseEdition',
    seats = 'seats',
    active = 'active',
    passwordServerEnabled = 'passwordServerEnabled'
}

const LicenseAllocationPage = () => {
    // -------------------------------------------
    // ------------------ DATA -------------------
    // -------------------------------------------
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.LicenseAllocation);

    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('OrganizationName');
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [rows, setRows] = useState<IOrganizationLicenseInfoTableData[]>([]);
    const [tenantEntryRows, setTenantEntryRows] = useState<ITenantLicenseEntriesTableData[]>([]);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);
    const tenantLicense = useSelector((state: any) => state.licenseAllocation.tenantLicense);
    const orgLicense = useSelector((state: any) => state.licenseAllocation.organizationLicenses);
    const orgLicenseCount = useSelector((state: any) => state.licenseAllocation.orgLicenseCount);
    const licensesLoading = useSelector((state: any) => state.licenseAllocation.licensesLoading);
    const loadingData = useSelector((state: any) => state.basic.loadingData);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: false,
        actionMenuEnabled: false
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            dispatch(handleGetTenantLicenseData());
            setRows([]);
            dispatch(resetScroll());
        }
    };

    const allocateSeats = (row: IOrganizationLicenseInfoTableData) => () => {
        dispatch(getModalSwitch(true));

        let header = translation('ALLOCATE_LICENSE_DIALOG_HEADER');
        header = header.replace(translationVariable, row.organizationName);

        setModalData({
            headerTitle: header,
            modalSwitcher: modalSwitchState,
            form: (
                <AllocateSeatsDialog
                    orgId={row.organizationId}
                    orgName={row.organizationName}
                    hasLicense={row.hasLicense}
                    seats={row.seats}
                    active={row.active}
                    licenseEdition={row.licenseEdition}
                    passwordServerEnabled={row.passwordServerEnabled}
                    handleRefresh={handlePageRefreshFromDialog}
                    tenantLicense={tenantLicense}
                />
            )
        });
    };

    const getComplexLicenseEditionCell = (row: IOrganizationLicenseInfoTableData) => {
        return <TableCell>{row.hasLicense ? row.licenseEdition : translation('NONE')}</TableCell>;
    };

    const getComplexPasswordServerCell = (row: IOrganizationLicenseInfoTableData) => {
        return <TableCell>{row.passwordServerEnabled ? translation('YES') : translation('NO')}</TableCell>;
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.organizationName,
            label: 'ORG_NAME',
            type: 'linkWithData',
            onClick: allocateSeats,
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.organizationName,
        },
        {
            id: TableHeadEnum.licenseEdition,
            label: 'TYPE',
            type: 'getComplexCell',
            getComplexCell: getComplexLicenseEditionCell
        },
        {
            id: TableHeadEnum.active,
            label: 'ACTIVE_SEATS'
        },
        {
            id: TableHeadEnum.seats,
            label: 'SEATS'
        },
        {
            id: TableHeadEnum.passwordServerEnabled,
            label: 'PASSWORD_SERVER_ENABLED',
            type: 'getComplexCell',
            getComplexCell: getComplexPasswordServerCell
        }
    ];

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        dispatch(handleGetTenantLicenseData());
    }, []);

    useEffect(() => {
        // do the data for the grid
        if (!loadingData && orgLicense) {
            let newRows = orgLicense.map((info: IOrganizationLicenseInfo) => {
                let pwsEnabled: boolean = false;
                if (info.OrganizationId === defaultOrgId || info.PasswordServerEnabled) {
                    pwsEnabled = true;
                }

                const data: IOrganizationLicenseInfoTableData = {
                    organizationId: info.OrganizationId,
                    organizationName: info.OrganizationName,
                    hasLicense: info.HasLicense,
                    seats: info.Seats,
                    active: info.Active,
                    licenseEdition: info.LicenseEdition,
                    passwordServerEnabled: pwsEnabled
                };
                return data;
            });

            orgLicense.length === 0 ? setRows([]) : setRows(rows.concat(newRows));
        }
    }, [loadingData, orgLicense]);

    useEffect(() => {
        if (!licensesLoading) {
            // do the data for the top bar
            if (tenantLicense?.Entries?.length) {
                let entryRows = tenantLicense.Entries.map((entry: ITenantLicenseEntries) => {
                    const data: ITenantLicenseEntriesTableData = {
                        seats: entry.Seats,
                        name: entry.Name,
                        active: entry.Active,
                        seatsRemaining: entry.SeatsRemaining,
                        seatsAllocated: entry.SeatsAllocated
                    };
                    return data;
                });

                setTenantEntryRows(entryRows);
            }
        }
    }, [licensesLoading, tenantLicense]);

    // -------------------------------------------
    // --------------- FUNCTIONS -----------------
    // -------------------------------------------
    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        dispatch(handleGetTenantLicenseData());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        dispatch(handleGetOrgLicenseData(orderBy, order, infiniteScrollItemsFetchLimit, skipCount, isReset, activeFilters));
    };

    return (
        <>
            <ModalComponent {...modalData} />

            {tenantEntryRows.length > 0 && (
                <div className='rowcontainer licenseSummary'>
                    {tenantEntryRows.map((row) => (
                        <PasslyTableRow key={row.name}>
                            <PasslyTableCell>
                                <span>{row.name}: </span>
                                <span>
                                    <b>{row.active.toLocaleString()} </b>
                                </span>
                                <span>{translation('ASSIGNED')} / </span>
                                <span>
                                    <b>{row.seatsAllocated.toLocaleString()} </b>
                                </span>
                                <span>{translation('ALLOCATED')} / </span>
                                <span>
                                    <b>{row.seats.toLocaleString()} </b>
                                </span>
                                <span>{translation('LICENSED')}</span>
                            </PasslyTableCell>
                        </PasslyTableRow>
                    ))}
                </div>
            )}

            <PasslyTable
                rows={rows}
                setRows={setRows}
                order={order}
                orderBy={orderBy}
                handleOrderChange={createSortHandler}
                headerItems={headCells}
                rowsCount={orgLicenseCount}
                onFetch={fetchMoreData}
                withCheckboxes={false}
                emptyListHelper={translation('EMPTY_ORG_LIST')}
                loadingData={loadingData}
            />
        </>
    );
};

export default LicenseAllocationPage;
